import { Application, Container } from 'pixi.js-legacy';
import { CONFIG } from '../config';
import { PixiLayers } from './PixiLayers';
import { bossTextureCache } from './utils/BossTextureCache';
import { speedLineTextureCache } from './utils/SpeedLineTextureCache';

export class PixiRenderer {
    private app!: Application;
    private canvas!: HTMLCanvasElement;
    private layers!: PixiLayers;
    private canvasWidth: number = CONFIG.CANVAS_WIDTH;
    private canvasHeight: number = CONFIG.CANVAS_HEIGHT;
    public isLandscape: boolean = false;
    public onOrientationChange: ((isLandscape: boolean, wasLandscape: boolean) => void) | null = null;
    private resizeTimeout: ReturnType<typeof setTimeout> | null = null;
    private lastContainerWidth: number = 0;
    private lastContainerHeight: number = 0;
    private initialized: boolean = false;
    private resizeHandler: (() => void) | null = null;
    private orientationHandler: (() => void) | null = null;
    private viewportResizeHandler: (() => void) | null = null;
    private visibilityHandler: (() => void) | null = null;

    async init(canvas: HTMLCanvasElement, width: number, height: number): Promise<void> {
        if (this.initialized) return;

        this.canvas = canvas;
        this.canvasWidth = width;
        this.canvasHeight = height;

        const baseWidth = width;
        const baseHeight = height;

        // iOS Safari: 해상도를 1로 고정하여 메모리 사용량 대폭 감소
        const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
        // iOS: resolution 1 고정 (메모리 사용량 최소화)
        // 다른 플랫폼: devicePixelRatio 사용
        const maxResolution = isIOS ? 1 : (window.devicePixelRatio || 1);

        // PixiJS v7: 생성자에서 옵션 전달 (v8의 app.init() 대신)
        // forceCanvas: true로 WebGL 대신 Canvas 2D 렌더러 강제 사용 (iOS 호환성)
        this.app = new Application({
            view: this.canvas,
            width: baseWidth,
            height: baseHeight,
            backgroundColor: 0x000000,
            resolution: maxResolution,
            autoDensity: true,
            antialias: false, // 모든 플랫폼에서 antialias 비활성화
            powerPreference: isIOS ? 'low-power' : 'high-performance',
            forceCanvas: true, // Canvas 2D 렌더러 강제 (WebGL 대신) - iOS Safari 호환성
        });

        // FPS 제한 설정 (120Hz 모니터에서도 60FPS로 제한)
        this.app.ticker.maxFPS = 60;

        this.layers = new PixiLayers(this.app.stage);
        this.setupCanvas();
        this.setupEventListeners();

        // 렌더러가 완전히 준비된 후 텍스쳐 캐시 초기화
        // Canvas 2D 렌더러의 경우 첫 렌더링 후 컨텍스트가 확실히 준비됨
        await this.initializeTextureCaches();

        this.initialized = true;
    }

    /**
     * 텍스처 캐시 초기화 (렌더러 준비 후)
     * Canvas 2D 렌더러가 완전히 준비된 후 RenderTexture를 생성해야 함
     */
    private async initializeTextureCaches(): Promise<void> {
        // 첫 프레임 렌더링을 강제하여 Canvas 2D 컨텍스트 확보
        try {
            this.app.renderer.render(this.app.stage);
        } catch (e) {
            console.warn('Initial render failed:', e);
        }

        // requestAnimationFrame으로 다음 프레임까지 대기
        await new Promise<void>(resolve => {
            requestAnimationFrame(() => {
                // 텍스쳐 캐시 초기화
                try {
                    bossTextureCache.initialize(this.app);
                } catch (e) {
                    console.warn('Boss texture cache initialization failed:', e);
                }

                try {
                    speedLineTextureCache.initialize(this.app);
                } catch (e) {
                    console.warn('Speed line texture cache initialization failed:', e);
                }

                resolve();
            });
        });
    }

    private setupEventListeners(): void {
        this.resizeHandler = (): void => {
            if (this.resizeTimeout !== null) {
                clearTimeout(this.resizeTimeout);
            }
            this.resizeTimeout = setTimeout(() => {
                this.setupCanvas();
            }, 100);
        };

        this.orientationHandler = (): void => {
            if (this.resizeTimeout !== null) {
                clearTimeout(this.resizeTimeout);
            }
            this.resizeTimeout = setTimeout(() => {
                this.setupCanvas();
            }, 300);
        };

        window.addEventListener('resize', this.resizeHandler);
        window.addEventListener('orientationchange', this.orientationHandler);

        if (window.visualViewport) {
            this.viewportResizeHandler = this.resizeHandler;
            window.visualViewport.addEventListener('resize', this.viewportResizeHandler);
        }

        // Canvas 렌더러를 사용하므로 WebGL 컨텍스트 관련 이벤트는 필요 없음
        // 하지만 visibility 변경 시 렌더러 리프레시는 여전히 유용
        this.visibilityHandler = (): void => {
            if (document.visibilityState === 'visible' && this.app?.renderer) {
                // 페이지가 다시 보일 때 렌더러 강제 리프레시
                try {
                    this.app.renderer.render(this.app.stage);
                } catch (e) {
                    console.warn('Canvas render refresh failed:', e);
                }
            }
        };
        document.addEventListener('visibilitychange', this.visibilityHandler);
    }

    destroy(): void {
        // 타이머 정리
        if (this.resizeTimeout !== null) {
            clearTimeout(this.resizeTimeout);
            this.resizeTimeout = null;
        }

        // 이벤트 리스너 제거
        if (this.resizeHandler) {
            window.removeEventListener('resize', this.resizeHandler);
            this.resizeHandler = null;
        }
        if (this.orientationHandler) {
            window.removeEventListener('orientationchange', this.orientationHandler);
            this.orientationHandler = null;
        }
        if (this.viewportResizeHandler && window.visualViewport) {
            window.visualViewport.removeEventListener('resize', this.viewportResizeHandler);
            this.viewportResizeHandler = null;
        }
        if (this.visibilityHandler) {
            document.removeEventListener('visibilitychange', this.visibilityHandler);
            this.visibilityHandler = null;
        }

        // 텍스쳐 캐시 정리
        bossTextureCache.destroy();
        speedLineTextureCache.destroy();

        // PixiJS 정리
        if (this.layers) {
            this.layers.clearAll();
        }
        if (this.app) {
            this.app.destroy(true, { children: true, texture: true });
        }

        this.onOrientationChange = null;
        this.initialized = false;
    }

    setupCanvas(): void {
        const container = this.canvas.parentElement;
        if (!container) return;

        let containerWidth: number;
        let containerHeight: number;
        if (window.visualViewport) {
            containerWidth = window.visualViewport.width;
            containerHeight = window.visualViewport.height;
        } else {
            containerWidth = container.clientWidth;
            containerHeight = container.clientHeight;
        }

        const wasLandscape = this.isLandscape;
        const newIsLandscape = containerWidth > containerHeight;
        const orientationChanged = wasLandscape !== newIsLandscape;

        // Skip only if nothing changed
        if (!orientationChanged &&
            containerWidth === this.lastContainerWidth &&
            containerHeight === this.lastContainerHeight) {
            return;
        }
        this.lastContainerWidth = containerWidth;
        this.lastContainerHeight = containerHeight;
        this.isLandscape = newIsLandscape;

        const baseWidth = this.isLandscape ? CONFIG.CANVAS_WIDTH_LANDSCAPE : CONFIG.CANVAS_WIDTH;
        const baseHeight = this.isLandscape ? CONFIG.CANVAS_HEIGHT_LANDSCAPE : CONFIG.CANVAS_HEIGHT;
        const aspectRatio = baseWidth / baseHeight;

        let canvasWidth: number;
        let canvasHeight: number;

        // Floor container dimensions to avoid fractional pixels
        containerWidth = Math.floor(containerWidth);
        containerHeight = Math.floor(containerHeight);

        const containerRatio = containerWidth / containerHeight;
        if (containerRatio > aspectRatio) {
            // Container is wider - fit to height
            canvasHeight = containerHeight;
            canvasWidth = Math.floor(containerHeight * aspectRatio);
        } else {
            // Container is taller - fit to width
            canvasWidth = containerWidth;
            canvasHeight = Math.floor(containerWidth / aspectRatio);
        }

        // Resize PixiJS renderer first (this will set canvas style due to autoDensity)
        if (this.app && this.app.renderer) {
            this.app.renderer.resize(baseWidth, baseHeight);
        }

        // Override PixiJS autoDensity style with our calculated dimensions
        this.canvas.style.width = `${canvasWidth}px`;
        this.canvas.style.height = `${canvasHeight}px`;

        // Update stored dimensions
        this.canvasWidth = baseWidth;
        this.canvasHeight = baseHeight;

        if (orientationChanged && this.onOrientationChange !== null) {
            this.onOrientationChange(this.isLandscape, wasLandscape);
        }
    }

    clear(): void {
        // PixiJS handles clearing automatically
        // But we can reset layer contents if needed
    }

    getCanvasWidth(): number {
        return this.isLandscape ? CONFIG.CANVAS_WIDTH_LANDSCAPE : CONFIG.CANVAS_WIDTH;
    }

    getCanvasHeight(): number {
        return this.isLandscape ? CONFIG.CANVAS_HEIGHT_LANDSCAPE : CONFIG.CANVAS_HEIGHT;
    }

    getCanvas(): HTMLCanvasElement {
        return this.canvas;
    }

    getApp(): Application {
        return this.app;
    }

    getStage(): Container {
        return this.app.stage;
    }

    getLayers(): PixiLayers {
        return this.layers;
    }

    render(): void {
        // PixiJS automatically renders via ticker
        // For manual rendering:
        // this.app.render();
    }

    /**
     * PixiJS ticker 일시정지 (메모리 누수 방지)
     */
    pause(): void {
        if (this.app && this.app.ticker) {
            this.app.ticker.stop();
        }
    }

    /**
     * PixiJS ticker 재개
     */
    resume(): void {
        if (this.app && this.app.ticker) {
            this.app.ticker.start();
        }
    }

    isInitialized(): boolean {
        return this.initialized;
    }
}
